import { Button } from 'react-aria-components';
import { useEffect, useRef } from 'react';
import { useState } from 'react';
import { renderMarkdown } from '~/utilities';

interface CollapsibleContentProps {
  content: string;
}

const MAX_HEIGHT = '200px';

export const CollapsibleContent = ({ content }: CollapsibleContentProps) => {
  const [showFullBio, setShowFullBio] = useState<boolean>(false);
  const [maxHeight, setMaxHeight] = useState(MAX_HEIGHT);
  const contentRef = useRef<HTMLDivElement>(null);
  const sanitizedHtml = renderMarkdown(content);

  useEffect(() => {
    if (showFullBio) {
      // Wait till next render to get the correct height of the content, plus a buffer
      setTimeout(() => {
        if (contentRef.current) {
          setMaxHeight(`${contentRef.current.scrollHeight + 24}px`);
        }
      }, 0);
      return;
    }

    setMaxHeight(MAX_HEIGHT);
  }, [showFullBio]);

  const handleToggle = (e: React.MouseEvent | React.KeyboardEvent) => {
    // Prevent toggling if the event target is a link
    if ((e.target as HTMLElement).closest('a')) {
      e.stopPropagation();
      return;
    }
    // For keyboard, only toggle on Enter or Space
    if ('key' in e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
    }
    setShowFullBio((prev) => !prev);
  };

  //collapsible-content-container is wrapped in an extra block level element to get the correct height
  return (
    <div>
      <div
        data-testid="collapsible-content-container"
        ref={contentRef}
        className="relative overflow-hidden leading-normal transition-[max-height] duration-300 ease-in-out"
        style={{ maxHeight }}
        role="button"
        tabIndex={0}
        onClick={handleToggle}
        onKeyUp={handleToggle}
        aria-expanded={showFullBio}
      >
        <div
          className="text-lg"
          data-testid="markdown-content-container"
          // The 'sanitizedHtml' variable has been sanitized with the 'sanitize-html' package before setting html here.
          dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        />
        {!showFullBio && (
          <div className="z-90 h-100 absolute bottom-0 right-0 top-0 w-full bg-gradient-to-t from-white to-40%" />
        )}
      </div>
      <Button
        data-testid="collapsible-content-toggle-button"
        className="focus-ring w-100 mx-auto flex p-1 text-md text-brand-80"
        onPress={() => setShowFullBio(!showFullBio)}
      >
        {showFullBio ? 'See less' : 'See more'}
      </Button>
    </div>
  );
};
